import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Box,
  Chip,
  TablePagination,
  CircularProgress,
  Alert,
  Switch,
} from '@mui/material';
import { Edit, Delete as DeleteIcon, Visibility, Difference } from '@mui/icons-material';
import {
  useGetBomItemsByBomByBomIdQuery,
  useDeleteBomItemsByIdMutation,
  useGetBomItemAuditTrailsByBomItemByBomItemIdQuery,
  usePatchBomItemAuditTrailsByIdFirstApprovalMutation,
  usePatchBomItemAuditTrailsByIdSecondApprovalMutation,
  usePatchBomItemsByIdFirstApprovalMutation,
  usePatchBomItemsByIdSecondApprovalMutation,
} from '../../../redux/vmsApi';
import { useAppSelector } from '../../../redux/hooks';

const getThresholdStyle = (current: number, initial: number): { bgcolor: string; color: string; label: string } => {
  if (!initial || initial === 0) return { bgcolor: 'transparent', color: 'inherit', label: '' };
  const pct = (current / initial) * 100;
  if (pct < 20) return { bgcolor: '#ffebee', color: '#c62828', label: `${pct.toFixed(0)}%` };
  if (pct < 50) return { bgcolor: '#fff3e0', color: '#e65100', label: `${pct.toFixed(0)}%` };
  if (pct < 80) return { bgcolor: '#fffde7', color: '#f57f17', label: `${pct.toFixed(0)}%` };
  return { bgcolor: '#e8f5e9', color: '#2e7d32', label: `${pct.toFixed(0)}%` };
};

interface BomItemsListProps {
  bomId: string;
  onEdit: (item: any) => void;
}

interface Filters {
  itemCode: string;
  itemDescription: string;
  itemUnit: string;
  itemBudgetedCost: string;
  itemQuantity: string;
}

const BomItemsList: React.FC<BomItemsListProps> = ({ bomId, onEdit }) => {
  const currentUser = useAppSelector((state) => state.auth.user);
  const canManageBomItems = !!currentUser?.roles?.includes('super-user');
  const canFirstApproveBomItems = !!currentUser?.roles?.includes('qs');
  const canSecondApproveBomItems =
    !!currentUser?.roles?.includes('qs') || !!currentUser?.roles?.includes('auditor');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<Filters>({
    itemCode: '',
    itemDescription: '',
    itemUnit: '',
    itemBudgetedCost: '',
    itemQuantity: '',
  });
  const [apiFilters, setApiFilters] = useState<any>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any | null>(null);
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [selectedDiffTrail, setSelectedDiffTrail] = useState<any | null>(null);

  const {
    data: itemsResponse = [],
    isLoading,
    error,
    refetch: refetchBomItems,
  } = useGetBomItemsByBomByBomIdQuery({
    bomId,
    ...apiFilters,
    limit: pageSize,
    skip: page * pageSize,
  });

  const [deleteItem, { isLoading: isDeleting }] = useDeleteBomItemsByIdMutation();
  const [approveFirst, { isLoading: isApprovingFirst }] = usePatchBomItemAuditTrailsByIdFirstApprovalMutation();
  const [approveSecond, { isLoading: isApprovingSecond }] = usePatchBomItemAuditTrailsByIdSecondApprovalMutation();
  const [approveItemFirst, { isLoading: isApprovingItemFirst }] = usePatchBomItemsByIdFirstApprovalMutation();
  const [approveItemSecond, { isLoading: isApprovingItemSecond }] = usePatchBomItemsByIdSecondApprovalMutation();
  const {
    data: auditTrails = [],
    isLoading: isLoadingTrails,
    refetch: refetchTrails,
  } = useGetBomItemAuditTrailsByBomItemByBomItemIdQuery(
    {
      bomItemId: selectedHistoryItem?.id ?? '',
      limit: 200,
      skip: 0,
    },
    {skip: !historyDialogOpen || !selectedHistoryItem?.id},
  );

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    const newFilters: any = {};
    if (filters.itemCode) newFilters.itemCode = filters.itemCode;
    if (filters.itemDescription) newFilters.itemDescription = filters.itemDescription;
    if (filters.itemUnit) newFilters.itemUnit = filters.itemUnit;
    if (filters.itemBudgetedCost) newFilters.itemBudgetedCost = parseFloat(filters.itemBudgetedCost);
    if (filters.itemQuantity) newFilters.itemQuantity = parseFloat(filters.itemQuantity);
    
    setApiFilters(newFilters);
    setPage(0);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedItemId(id);
    setDeleteDialogOpen(true);
  };

  const handleOpenHistory = (item: any) => {
    setSelectedHistoryItem(item);
    setHistoryDialogOpen(true);
  };

  const extractChangedKeys = (oldBomItem: any, newBomItem: any): string[] => {
    const trackedKeys = [
      'itemCode',
      'itemDescription',
      'itemUnit',
      'isService',
      'isVAT',
      'itemBudgetedCost',
      'itemQuantity',
      'itemCurrentValue',
      'initialQuantity',
      'itemInitialValue',
      'procuredQuantity',
      'procuredValue',
    ];

    return trackedKeys.filter(key => {
      const before = oldBomItem?.[key];
      const after = newBomItem?.[key];
      return JSON.stringify(before) !== JSON.stringify(after);
    });
  };

  const handleApprovalToggle = async (trailId: string, stage: 'first' | 'second') => {
    try {
      if (stage === 'first') {
        await approveFirst({id: trailId, body: {}}).unwrap();
      } else {
        await approveSecond({id: trailId, body: {}}).unwrap();
      }
      await refetchTrails();
    } catch (err) {
      console.error('Failed to capture approval:', err);
    }
  };

  const handleItemApprovalToggle = async (itemId: string, stage: 'first' | 'second') => {
    try {
      if (stage === 'first') {
        await approveItemFirst({
          id: itemId,
          body: {},
        }).unwrap();
      } else {
        await approveItemSecond({
          id: itemId,
          body: {},
        }).unwrap();
      }

      await refetchBomItems();
    } catch (err) {
      console.error('Failed to capture BOM item approval:', err);
    }
  };

  const handleConfirmDelete = async () => {
    if (!canManageBomItems) {
      setDeleteDialogOpen(false);
      setSelectedItemId(null);
      return;
    }

    if (selectedItemId) {
      try {
        await deleteItem({ id: selectedItemId }).unwrap();
        setDeleteDialogOpen(false);
        setSelectedItemId(null);
        await refetchBomItems();
      } catch (err) {
        console.error('Failed to delete BOM item:', err);
      }
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error loading BOM items: {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  const items = Array.isArray(itemsResponse) ? itemsResponse : [];

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 2 }}>
          <TextField
            label="Item Code"
            size="small"
            value={filters.itemCode}
            onChange={(e) => handleFilterChange('itemCode', e.target.value)}
            placeholder="Search by item code"
          />
          <TextField
            label="Item Description"
            size="small"
            value={filters.itemDescription}
            onChange={(e) => handleFilterChange('itemDescription', e.target.value)}
            placeholder="Search by description"
          />
          <TextField
            label="Unit"
            size="small"
            value={filters.itemUnit}
            onChange={(e) => handleFilterChange('itemUnit', e.target.value)}
            placeholder="Search by unit"
          />
          <TextField
            label="Rate"
            type="number"
            size="small"
            value={filters.itemBudgetedCost}
            onChange={(e) => handleFilterChange('itemBudgetedCost', e.target.value)}
            placeholder="Filter by rate"
          />
          <TextField
            label="Quantity"
            type="number"
            size="small"
            value={filters.itemQuantity}
            onChange={(e) => handleFilterChange('itemQuantity', e.target.value)}
            placeholder="Filter by quantity"
          />
        </Box>
        <Button variant="contained" onClick={handleApplyFilters} sx={{ mr: 1 }}>
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setFilters({
              itemCode: '',
              itemDescription: '',
              itemUnit: '',
              itemBudgetedCost: '',
              itemQuantity: '',
            });
            setApiFilters({});
            setPage(0);
          }}
        >
          Clear Filters
        </Button>
      </Paper>

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Item Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Unit</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Rate</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">VAT</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Initial Qty</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Remaining Qty</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Procured Qty</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Budget</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Remaining</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Procured Value</TableCell>
              {/* <TableCell sx={{ fontWeight: 'bold' }} align="center">Approval Status</TableCell> */}
              <TableCell sx={{ fontWeight: 'bold' }} align="center">1st QS Approval</TableCell>
              {/* <TableCell sx={{ fontWeight: 'bold' }} align="center">2nd Approval (Optional)</TableCell> */}
              <TableCell sx={{ fontWeight: 'bold' }} align="center">History</TableCell>
              {canManageBomItems && (
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length > 0 ? (
              items.map((item, index) => (
                <TableRow
                  key={item.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
                    '&:hover': { backgroundColor: '#f0f0f0' },
                  }}
                >
                  <TableCell>{item.itemCode}</TableCell>
                  <TableCell>{item.itemDescription}</TableCell>
                  <TableCell>{item.itemUnit}</TableCell>
                  <TableCell align="right">{item.itemBudgetedCost?.toFixed(2)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={item.isVAT ? 'VAT' : 'No VAT'}
                      color={item.isVAT ? 'warning' : 'default'}
                      variant={item.isVAT ? 'filled' : 'outlined'}
                      sx={item.isVAT ? { fontWeight: 700 } : {}}
                    />
                  </TableCell>
                  <TableCell align="right">{item.initialQuantity}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: getThresholdStyle(item.itemQuantity, item.initialQuantity).bgcolor }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <span>{item.itemQuantity}</span>
                      {item.initialQuantity > 0 && (
                        <span style={{ fontSize: '0.7rem', color: getThresholdStyle(item.itemQuantity, item.initialQuantity).color, fontWeight: 700 }}>
                          {getThresholdStyle(item.itemQuantity, item.initialQuantity).label} remaining
                        </span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {(item as any).procuredQuantity != null ? Number((item as any).procuredQuantity).toFixed(2) : '—'}
                  </TableCell>
                  <TableCell align="right">{item.itemInitialValue?.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: getThresholdStyle(item.itemCurrentValue, item.itemInitialValue).bgcolor }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <span>{item.itemCurrentValue?.toFixed(2)}</span>
                      {item.itemInitialValue > 0 && (
                        <span style={{ fontSize: '0.7rem', color: getThresholdStyle(item.itemCurrentValue, item.itemInitialValue).color, fontWeight: 700 }}>
                          {getThresholdStyle(item.itemCurrentValue, item.itemInitialValue).label} remaining
                        </span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {(item as any).procuredValue != null ? Number((item as any).procuredValue).toFixed(2) : '—'}
                  </TableCell>
                  {/* <TableCell align="center">
                    {Boolean((item as any).firstApproval?.approved || (item as any).isApproved) ? (
                      <Chip size="small" color="success" label="Fully Approved" />
                    ) : (
                      <Chip size="small" color="warning" label="Pending QS Approval" />
                    )}
                  </TableCell> */}
                  <TableCell align="center">
                    {(item as any).firstApproval?.approved ? (
                      <Chip
                        size="small"
                        color="success"
                        label={`Approved by ${(item as any).firstApproval?.user?.name || (item as any).firstApproval?.user?.email || 'QS'}`}
                      />
                    ) : canFirstApproveBomItems && item.id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Switch
                          checked={false}
                          disabled={isApprovingItemFirst}
                          onChange={() => handleItemApprovalToggle(item.id as string, 'first')}
                        />
                        <span>Approve</span>
                      </Box>
                    ) : (
                      <span>—</span>
                    )}
                  </TableCell>
                  {/* <TableCell align="center">
                    {(item as any).secondApproval?.approved ? (
                      <Chip
                        size="small"
                        color="success"
                        label={`Approved by ${(item as any).secondApproval?.user?.name || (item as any).secondApproval?.user?.email || 'Reviewer'}`}
                      />
                    ) : canSecondApproveBomItems && item.id && Boolean((item as any).firstApproval?.approved) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Switch
                          checked={false}
                          disabled={isApprovingItemSecond}
                          onChange={() => handleItemApprovalToggle(item.id as string, 'second')}
                        />
                        <span>Approve</span>
                      </Box>
                    ) : (
                      <span>—</span>
                    )}
                  </TableCell> */}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenHistory(item)}
                      title="View audit trail"
                      sx={{ color: '#455a64' }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                  {canManageBomItems && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(item)}
                          title="Edit"
                          sx={{ color: '#1976d2' }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => item.id && handleDeleteClick(item.id)}
                          title="Delete"
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={canManageBomItems ? 16 : 15} align="center" sx={{ py: 3, color: '#999' }}>
                  No items found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={items.length > 0 ? (page + 1) * pageSize + (items.length < pageSize ? 0 : 1) : 0}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete BOM Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this BOM item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          BOM Item Audit Trail - {selectedHistoryItem?.itemDescription || selectedHistoryItem?.itemCode || ''}
        </DialogTitle>
        <DialogContent>
          {isLoadingTrails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item Description</TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell>Date</TableCell>
                    {/* <TableCell>User</TableCell> */}
                    <TableCell align="center">Change</TableCell>
                    <TableCell>Supporting Documents</TableCell>
                    <TableCell>First Approval</TableCell>
                    <TableCell>Second Approval</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(auditTrails || []).length > 0 ? (
                    (auditTrails as any[]).map((trail) => {
                      const changedKeys = extractChangedKeys(trail.oldBomItem, trail.newBomItem);
                      const firstApproved = Boolean(trail.firstApproval?.approved);
                      const secondApproved = Boolean(trail.secondApproval?.approved);
                      return (
                        <TableRow key={trail.id}>
                          <TableCell>{trail.newBomItem?.itemDescription || trail.oldBomItem?.itemDescription || '—'}</TableCell>
                          <TableCell>
                            {(() => {
                              const trigger = trail.auditTrigger || trail.trigger || 'ManualAdjustment';
                              return trigger === 'Ordered' && trail.purchaseOrderNumber
                                ? `${trigger}(${trail.purchaseOrderNumber})`
                                : trigger;
                            })()}
                          </TableCell>
                          <TableCell>
                            {trail.createdAt ? new Date(trail.createdAt).toLocaleString() : '—'}
                          </TableCell>
                          {/* <TableCell>
                            {trail.user?.name || trail.user?.email || trail.user?.id || 'System'}
                          </TableCell> */}
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              title="View changed fields"
                              onClick={() => {
                                setSelectedDiffTrail(trail);
                                setDiffDialogOpen(true);
                              }}
                            >
                              <Difference fontSize="small" />
                            </IconButton>
                            <Chip
                              size="small"
                              sx={{ ml: 1 }}
                              label={`${changedKeys.length} field(s)`}
                              color={changedKeys.length > 0 ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {Array.isArray((trail as any).attachments) && (trail as any).attachments.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {(trail as any).attachments.map((attachment: any, index: number) => {
                                  const href = attachment?.url || attachment?.name;
                                  return href ? (
                                    <a
                                      key={`${trail.id}-attachment-${index}`}
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#1976d2', textDecoration: 'none' }}
                                    >
                                      {attachment?.name || `Document ${index + 1}`}
                                    </a>
                                  ) : null;
                                })}
                              </Box>
                            ) : (
                              <span>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {firstApproved ? (
                              <Chip
                                size="small"
                                color="success"
                                label={`Approved by ${trail.firstApproval?.user?.name || trail.firstApproval?.user?.email || 'User'}`}
                              />
                            ) : canFirstApproveBomItems ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Switch
                                  checked={false}
                                  disabled={isApprovingFirst}
                                  onChange={() => handleApprovalToggle(trail.id, 'first')}
                                />
                                <span>Approve</span>
                              </Box>
                            ) : (
                              <span>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {secondApproved ? (
                              <Chip
                                size="small"
                                color="success"
                                label={`Approved by ${trail.secondApproval?.user?.name || trail.secondApproval?.user?.email || 'User'}`}
                              />
                            ) : canSecondApproveBomItems && firstApproved ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Switch
                                  checked={false}
                                  disabled={isApprovingSecond}
                                  onChange={() => handleApprovalToggle(trail.id, 'second')}
                                />
                                <span>Approve</span>
                              </Box>
                            ) : (
                              <span>—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                        No audit history found for this item.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={diffDialogOpen} onClose={() => setDiffDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Changed Fields</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Old Value</TableCell>
                  <TableCell>New Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {extractChangedKeys(selectedDiffTrail?.oldBomItem, selectedDiffTrail?.newBomItem).map((field) => (
                  <TableRow key={field}>
                    <TableCell>
                      <Chip size="small" label={field} />
                    </TableCell>
                    <TableCell sx={{ bgcolor: '#ffebee' }}>
                      {String(selectedDiffTrail?.oldBomItem?.[field] ?? '—')}
                    </TableCell>
                    <TableCell sx={{ bgcolor: '#e8f5e9' }}>
                      {String(selectedDiffTrail?.newBomItem?.[field] ?? '—')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiffDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BomItemsList;
